import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Subject, forkJoin } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { expandIn } from '../../animations';
import { BannerNotificationsService, SpinnerService } from '../../../xform-compat';
import { AlertsDataService } from '../../../core/data-services/alerts-data.service';
import { AlertsService, AuthenticationService, DateTimeService } from '../../../core/services';
import { CompanyService } from '../../../core/data-services/company';
import { CompanyMemberService } from '../../../core/data-services/company';
import { UserService, UserGroupsService } from '../../../core/data-services';
import { CONFIG } from '../../../config';
import { BasicUser, CompanyAlert, CompanyBasic } from '../../models';
import { CompanyMember } from '../../models/company/company-member.model';

/**
 * Shared alert form component used for creating and editing alerts.
 * Opened as a component-based modal via NgbModal.open(AlertFormComponent).
 * Supports both my-alerts (with company selector) and company-alerts (companyId pre-set) contexts.
 */
@Component({
    selector: 'rms-alert-form',
    templateUrl: './alert-form.component.html',
    styleUrls: ['./alert-form.component.scss'],
    standalone: false,
    animations: [expandIn()]
})
export class AlertFormComponent implements OnInit, OnDestroy {

  /** When provided, the company is pre-selected and the company field is hidden. */
  @Input() companyId: number;

  /** The alert record to edit. Null for create mode. */
  @Input() alert: any;

  /** Whether the form is in edit mode. */
  @Input() isEditMode = false;

  // Form model
  public formAlert: any = {};
  public alertHour: string;
  public ampm = 'am';
  public dateTimeInPast = false;
  public selectedDate: string;
  public isCompanyMemberAlertEdit = false;

  // Company typeahead
  public companyList: Array<CompanyBasic> = [];
  public companyTypeahead = new Subject<string>();

  // Users/groups typeahead
  public memberList: Array<any> = [];
  public usersTypeahead = new Subject<string>();

  // Company members (for primary/secondary checkboxes)
  public companyMembers: CompanyMember;

  // Current user
  public currentUserData: BasicUser;

  // Options
  public manualAlertFrequencyOptions = CONFIG.alertOptions.manualAlertOptions;
  public automaticAlertFrequencyOptions = CONFIG.alertOptions.automaticAlertOptions;

  private recordId: number;
  private destroy$ = new Subject<void>();

  constructor(
    public activeModal: NgbActiveModal,
    private alertsDataService: AlertsDataService,
    private alertsService: AlertsService,
    private companyService: CompanyService,
    private companyMemberService: CompanyMemberService,
    private userGroupService: UserGroupsService,
    private userService: UserService,
    private spinnerService: SpinnerService,
    private bannerNotificationService: BannerNotificationsService,
    private translateService: TranslateService,
    private authenticationService: AuthenticationService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadCurrentUser();
    this.usersAndUserGroupsAutocomplete();
    this.companyAutocomplete();
    this.initForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Processes and submits the form. Validates the form, formats data for the API,
   * checks that the datetime is not in the past, then creates or updates the alert.
   */
  public processRecord(form: NgForm): void {
    if (!form.valid) {
      return;
    }

    this.spinnerService.start();
    this.dateTimeInPast = false;

    if (!this.isCompanyMemberAlertEdit) {
      this.formatFormDataForApi(form);
      if (!this.validateDateTime(form)) {
        this.dateTimeInPast = true;
        this.spinnerService.stop();
        return;
      }
    }

    const companyId = form.value.companyId || this.companyId;

    if (this.isEditMode) {
      form.value.id = this.recordId;
      this.alertsDataService.updateAlert(companyId, this.recordId, form.value)
        .pipe(
          finalize(() => this.spinnerService.stop()),
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: () => this.onSuccess(),
          error: (error) => this.bannerNotificationService.error(error)
        });
    } else {
      this.alertsDataService.createAlert(companyId, form.value)
        .pipe(
          finalize(() => this.spinnerService.stop()),
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: () => this.onSuccess(),
          error: (error) => this.bannerNotificationService.error(error)
        });
    }
  }

  /**
   * Formats the hour input to ensure valid HH:mm format (1-12 hours, 0-59 minutes).
   */
  public formatHour(): void {
    if (!this.alertHour) {
      return;
    }

    let cleaned = this.alertHour.replace(/[^\d:]/g, '');
    if (!cleaned.includes(':') && cleaned.length >= 2) {
      cleaned = cleaned.substring(0, 2) + ':' + cleaned.substring(2);
    }

    const parts = cleaned.split(':');
    let hours = parseInt(parts[0], 10) || 0;
    let minutes = parseInt(parts[1], 10) || 0;

    hours = Math.max(1, Math.min(12, hours));
    minutes = Math.max(0, Math.min(59, minutes));

    this.alertHour = String(hours).padStart(2, '0') + ':' + String(minutes).padStart(2, '0');

    if (this.formAlert.date) {
      const hour24 = this.ampm === 'pm' && hours < 12 ? hours + 12 : (this.ampm === 'am' && hours === 12 ? 0 : hours);
      this.formAlert.date = this.formAlert.date.set({ hour: hour24, minute: minutes });
    }
  }

  /**
   * Handles company selection change. Resets primary/secondary checkboxes
   * and fetches company member data.
   */
  public handleCompanyChange(event: CompanyBasic): void {
    if (!event) {
      return;
    }
    this.formAlert.addPrimary = false;
    this.formAlert.addSecondary = false;
    this.loadCompanyMembers(event.id);
  }

  private loadCurrentUser(): void {
    const user = this.authenticationService.getLoggedUser();
    if (user) {
      this.currentUserData = {
        firstName: user.firstName,
        lastName: user.lastName,
        name: user.fullName,
        id: user.id
      };
    }
  }

  private initForm(): void {
    this.isCompanyMemberAlertEdit = false;

    if (this.isEditMode && this.alert) {
      this.populateEditForm(this.alert);
    } else {
      this.initCreateForm();
    }
  }

  private initCreateForm(): void {
    this.formAlert = new CompanyAlert();
    this.formAlert.assignedTo = [];
    this.formAlert.companyId = this.companyId || null;

    if (this.currentUserData) {
      this.formAlert.assignedTo.push(this.currentUserData);
    }

    this.alertHour = '08:00';
    this.ampm = 'am';
    this.dateTimeInPast = false;
    this.formAlert.repeatAlert = false;
    this.formAlert.alertFrequency = this.manualAlertFrequencyOptions.find(opt => opt.value === '1')?.value;
    this.formAlert.indefiniteInstance = true;
    this.formAlert.instanceCount = 2;
    this.formAlert.addPrimary = false;
    this.formAlert.addSecondary = false;
    this.companyMembers = null;

    // Set default date to next month and sync the time component
    const nextMonth = DateTime.now().toLocal().plus({ months: 1 });
    this.selectedDate = nextMonth.toFormat(CONFIG.dateFormat);
    this.formAlert.date = DateTime.fromFormat(this.selectedDate, CONFIG.dateFormat);
    this.formatHour();
    this.cd.detectChanges();

    // Load company members if companyId is provided
    this.loadCompanyMembers(this.companyId);
  }

  private populateEditForm(record: any): void {
    this.recordId = record.id;
    this.formAlert = { ...record };
    this.formAlert.companyId = record.company?.id ?? record.companyId;
    this.selectedDate = null;

    if ('alert' in record) {
      this.populateManualAlertEditForm(record);
    } else {
      this.populateAutomaticAlertEditForm(record);
    }
  }

  /**
   * Populates form fields for editing a manual alert (has nested .alert property).
   * Parses datetime, maps assigned users/groups, and loads company members.
   */
  private populateManualAlertEditForm(record: any): void {
    this.isCompanyMemberAlertEdit = false;

    // Parse the UTC datetime from the server and convert to local (EST/EDT)
    const utcDateTime = DateTime.fromFormat(
      record.alert.alertTriggerDatetime, CONFIG.dateTimeServerFormat, { zone: 'utc' }
    );
    const localDateTime = utcDateTime.setZone(CONFIG.formatEST);

    this.selectedDate = localDateTime.toFormat(CONFIG.dateFormat);
    this.formAlert.date = localDateTime;
    this.cd.detectChanges();

    const hourNum = localDateTime.hour;
    if (hourNum >= 0 && hourNum < 12) {
      this.ampm = 'am';
      this.alertHour = String(hourNum === 0 ? 12 : hourNum).padStart(2, '0') + ':' + String(localDateTime.minute).padStart(2, '0');
    } else {
      this.ampm = 'pm';
      this.alertHour = String(hourNum === 12 ? 12 : hourNum - 12).padStart(2, '0') + ':' + String(localDateTime.minute).padStart(2, '0');
    }
    this.formatHour();

    // Map form fields from record
    this.formAlert.description = record.alert.manualAlert?.alertText;
    this.formAlert.addPrimary = record.alert.addPrimary;
    this.formAlert.addSecondary = record.alert.addSecondary;
    this.formAlert.repeatAlert = record.alert.repeatAlert;
    this.formAlert.alertFrequency = record.alert.alertFrequency;
    this.formAlert.indefiniteInstance = record.alert.indefiniteInstance;
    this.formAlert.instanceCount = record.alert.instanceCount;

    // Build assignedTo from users and userGroups
    this.formAlert.assignedTo = [];
    record.alert.users?.forEach(item => {
      this.formAlert.assignedTo.push({ ...item, name: item.fullName });
    });
    record.alert.userGroups?.forEach(item => {
      this.formAlert.assignedTo.push(item);
    });

    // Populate company list for the dropdown if in my-alerts context
    if (!this.companyId && record.company) {
      this.companyList = [new CompanyBasic({
        id: Number(record.companyId ?? record.company.id),
        active: record.company.active,
        name: record.company.name,
        isPrivate: record.company.isPrivate
      })];
    }

    this.loadCompanyMembers(this.companyId || this.formAlert.companyId);
  }

  /**
   * Populates form fields for editing an automatic/company-member alert (flat structure).
   */
  private populateAutomaticAlertEditForm(record: any): void {
    this.isCompanyMemberAlertEdit = true;
    this.formAlert.alertFrequency = record.alertFrequency ? record.alertFrequency.toString() : '';
  }

  /**
   * Loads company member data for primary/secondary checkboxes.
   */
  private loadCompanyMembers(companyId: number): void {
    if (companyId) {
      this.companyMemberService.getCompanyMemberData(companyId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({ next: (data) => this.companyMembers = data });
    }
  }

  private companyAutocomplete(): void {
    this.companyTypeahead.pipe(
      distinctUntilChanged(),
      debounceTime(200),
      switchMap(term => this.companyService.searchCompanies({ q: term })),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (value) => this.companyList = value.records,
      error: () => this.companyList = []
    });
  }

  private usersAndUserGroupsAutocomplete(): void {
    this.usersTypeahead.pipe(
      distinctUntilChanged(),
      debounceTime(200),
      switchMap(term => forkJoin([
        this.userGroupService.autocompleteUserGroups(term),
        this.userService.autocompleteUsers(term)
      ])),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (value) => {
        this.memberList = value[0].records;
        this.memberList.push(...value[1].records);
        this.memberList.forEach(item => item.name = item.firstName ? item.firstName + ' ' + item.lastName : item.name);
        this.cd.markForCheck();
      },
      error: () => this.memberList = []
    });
  }

  /**
   * Transforms form data to the API format using the shared AlertsService.
   */
  private formatFormDataForApi(form: any): void {
    this.alertsService.getFormattedData(form, this.ampm);
  }

  private validateDateTime(form: any): boolean {
    return DateTime.fromFormat(form.value.alert.alertTriggerDatetime, CONFIG.dateTimeServerFormat, { zone: 'utc' }) > DateTime.now();
  }

  private onSuccess(): void {
    this.bannerNotificationService.success(
      this.translateService.instant(
        this.isEditMode ? 'MESSAGES.UPDATED' : 'MESSAGES.CREATED',
        { value: 'Record' }
      )
    );
    this.activeModal.close(true);
  }
}
